import { createHash } from "node:crypto";
import { existsSync, readdirSync, readFileSync, statSync } from "node:fs";
import { basename, join, relative } from "node:path";

const DEFAULT_MAX_BYTES = 48_000;
const DEFAULT_LIMIT = 12;
const IGNORED_DIRS = new Set([".git", ".next", ".turbo", "build", "dist", "node_modules"]);

export const LOCAL_SKILL_SOURCES = ["opencode", "claude", "codex", "agents", "workspace"] as const;

export type LocalSkillSource = (typeof LOCAL_SKILL_SOURCES)[number];

export interface LocalSkillCandidate {
  agentDomains: string[];
  id: string;
  path: string;
  reason: string;
  score: number;
  snippet: string;
  source: LocalSkillSource;
  title: string;
}

export interface DiscoverLocalSkillsOptions {
  context?: string;
  cwd?: string;
  home?: string;
  limit?: number;
  maxBytes?: number;
  roots?: Partial<Record<LocalSkillSource, string[]>>;
  sources?: readonly LocalSkillSource[];
}

const DOMAIN_KEYWORDS: Array<{ domain: string; pattern: RegExp }> = [
  { domain: "engineering", pattern: /\b(eval|benchmark|test|ci|code|github|repo|runtime|api|llm|model)\b/i },
  { domain: "product", pattern: /\b(customer|workflow|roadmap|prd|user|adoption|value|requirements?)\b/i },
  { domain: "operations", pattern: /\b(runbook|incident|sla|dashboard|superset|metric|monitor|ops)\b/i },
  { domain: "design", pattern: /\b(ui|ux|design|accessibility|component|visual)\b/i },
  { domain: "marketing", pattern: /\b(launch|campaign|positioning|gtm|content|story)\b/i },
  { domain: "sales", pattern: /\b(deal|pipeline|prospect|meddic|outreach|buyer)\b/i },
];

function hash(value: string, length = 10): string {
  return createHash("sha256").update(value).digest("hex").slice(0, length);
}

function safeStat(path: string) {
  try {
    return statSync(path);
  } catch {
    return null;
  }
}

function defaultRoots(input: { cwd: string; home: string }): Record<LocalSkillSource, string[]> {
  return {
    agents: [join(input.home, ".agents", "skills")],
    claude: [join(input.home, ".claude", "skills"), join(input.cwd, ".claude", "skills")],
    codex: [join(input.home, ".codex", "skills"), join(input.cwd, ".codex", "skills")],
    opencode: [
      join(input.home, ".opencode", "skills"),
      join(input.home, ".config", "opencode", "skills"),
      join(input.home, "Library", "Application Support", "opencode", "skills"),
      join(input.cwd, ".opencode", "skills"),
    ],
    workspace: [
      join(input.cwd, "skills"),
      join(input.cwd, ".agents", "skills"),
      join(input.cwd, ".orgx", "skills"),
    ],
  };
}

function walkSkillFiles(root: string, maxFiles = 200): string[] {
  const rootStats = safeStat(root);
  if (!rootStats) return [];
  if (rootStats.isFile()) return [root];
  if (!rootStats.isDirectory()) return [];

  const files: string[] = [];
  const stack = [root];
  while (stack.length > 0 && files.length < maxFiles) {
    const current = stack.pop();
    if (!current) continue;
    let entries;
    try {
      entries = readdirSync(current, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const entry of entries) {
      if (IGNORED_DIRS.has(entry.name)) continue;
      const path = join(current, entry.name);
      if (entry.isDirectory()) {
        stack.push(path);
      } else if (entry.isFile() && /\.(md|mdc|txt)$/i.test(entry.name)) {
        files.push(path);
      }
    }
  }
  return files;
}

function readWindow(path: string, maxBytes: number): string | null {
  const stats = safeStat(path);
  if (!stats?.isFile() || stats.size === 0) return null;
  try {
    const text = readFileSync(path, "utf8");
    return text.slice(0, maxBytes);
  } catch {
    return null;
  }
}

function titleFrom(path: string, text: string): string {
  const heading = text.match(/^#\s+(.+)$/m)?.[1]?.trim();
  if (heading) return heading.slice(0, 120);
  return basename(path).replace(/\.(md|mdc|txt)$/i, "").replace(/[-_]+/g, " ");
}

function snippetFrom(text: string): string {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("---") && !/^#+\s/.test(line));
  return lines.slice(0, 4).join(" ").replace(/\s+/g, " ").slice(0, 420);
}

function tokenize(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 3);
}

function scoreCandidate(text: string, context: string, source: LocalSkillSource): { reason: string; score: number } {
  const haystack = text.toLowerCase();
  const contextTokens = [...new Set(tokenize(context))];
  const contextHits = contextTokens.filter((token) => haystack.includes(token)).length;
  const explicitSkillSignal = /\b(skill|agent|workflow|instruction|rule|prompt|playbook)\b/i.test(text) ? 2 : 0;
  const sourceBoost = source === "opencode" ? 2 : source === "workspace" ? 1 : 0;
  const score = contextHits * 3 + explicitSkillSignal + sourceBoost;
  const reasonParts = [
    contextHits > 0 ? `${contextHits} context match${contextHits === 1 ? "" : "es"}` : "local skill file",
    sourceBoost > 0 ? `${source} source` : "",
  ].filter(Boolean);
  return { reason: reasonParts.join("; "), score };
}

function inferDomains(text: string, context: string): string[] {
  const joined = `${text}\n${context}`;
  const domains = DOMAIN_KEYWORDS
    .filter((entry) => entry.pattern.test(joined))
    .map((entry) => entry.domain);
  return domains.length > 0 ? [...new Set(domains)] : ["orchestrator"];
}

export function parseLocalSkillSources(value?: string): LocalSkillSource[] {
  if (!value?.trim() || value.trim().toLowerCase() === "all") {
    return [...LOCAL_SKILL_SOURCES];
  }
  const requested = value
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const invalid = requested.filter((source) => !LOCAL_SKILL_SOURCES.includes(source as LocalSkillSource));
  if (invalid.length > 0) {
    throw new Error(`Unsupported local skill source: ${invalid.join(", ")}. Use ${LOCAL_SKILL_SOURCES.join(", ")}, or all.`);
  }
  return [...new Set(requested)] as LocalSkillSource[];
}

export function discoverLocalSkills(options: DiscoverLocalSkillsOptions = {}): LocalSkillCandidate[] {
  const cwd = options.cwd ?? process.cwd();
  const home = options.home ?? process.env.HOME ?? "";
  const sources = options.sources ?? [...LOCAL_SKILL_SOURCES];
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
  const context = options.context?.trim() ?? "";
  const roots = defaultRoots({ cwd, home });
  for (const [source, overrides] of Object.entries(options.roots ?? {}) as Array<[LocalSkillSource, string[]]>) {
    roots[source] = overrides;
  }

  const seen = new Set<string>();
  const candidates: LocalSkillCandidate[] = [];
  for (const source of sources) {
    for (const root of roots[source] ?? []) {
      for (const path of walkSkillFiles(root)) {
        if (seen.has(path)) continue;
        seen.add(path);
        const text = readWindow(path, maxBytes);
        if (!text || /ORGX SKILL COMPOSED v1/.test(text)) continue;
        const title = titleFrom(path, text);
        const snippet = snippetFrom(text);
        const scored = scoreCandidate(`${title}\n${snippet}\n${text}`, context, source);
        candidates.push({
          agentDomains: inferDomains(`${title}\n${snippet}`, context),
          id: `${source}-${hash(path)}`,
          path,
          reason: scored.reason,
          score: scored.score,
          snippet,
          source,
          title,
        });
      }
    }
  }

  return candidates
    .sort((left, right) => right.score - left.score || left.title.localeCompare(right.title))
    .slice(0, Math.max(1, options.limit ?? DEFAULT_LIMIT));
}

export function selectLocalSkillCandidates(
  candidates: readonly LocalSkillCandidate[],
  selection: string,
): LocalSkillCandidate[] {
  const wanted = selection
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const selected: LocalSkillCandidate[] = [];
  for (const item of wanted) {
    const byIndex = /^\d+$/.test(item) ? candidates[Number(item) - 1] : undefined;
    const byId = candidates.find((candidate) => candidate.id === item);
    const match = byIndex ?? byId;
    if (!match) {
      throw new Error(`No local skill candidate matched '${item}'.`);
    }
    selected.push(match);
  }
  return [...new Map(selected.map((candidate) => [candidate.id, candidate])).values()];
}

export function buildLocalSkillExtensionContent(
  candidates: readonly LocalSkillCandidate[],
  context?: string,
): string {
  const lines = [
    "# Local Skill Preferences",
    "",
    "Use these opt-in local preferences when they are relevant to the current OrgX initiative. Do not override explicit user instructions or repo guardrails.",
  ];
  const trimmedContext = context?.trim();
  if (trimmedContext) {
    lines.push("", `Context: ${trimmedContext}`);
  }
  for (const candidate of candidates) {
    lines.push(
      "",
      `## ${candidate.title}`,
      "",
      `- Source: ${candidate.source} (${relative(process.cwd(), candidate.path)})`,
      `- Suggested agents: ${candidate.agentDomains.join(", ")}`,
      `- Preserve: ${candidate.snippet || "local workflow preference from this skill file."}`,
    );
  }
  return lines.join("\n");
}
